import { useState, useEffect, useContext } from 'react';
import RightPanel from '../components/chat/RightPanel';
import { scroller } from 'react-scroll';
import { useLocation } from 'react-router-dom';
import IBotMessage from '../models/iBotMessage';
import Chat from '../components/chat/Chat';
import Split from 'react-split';
import '../styles/ChatPage.css';
import Box from '@mui/material/Box';
import { MessageContext } from '../context/MessageContext';
import { useMediaQuery } from '@mui/material';
import { MobileConstants } from '../constants';
import { useLogger } from '../hooks/useLogger';
import { SearchTopic, SearchTopics } from '../enum/SearchTopics';

export interface IChatPageProps {
  scrollToNewest: boolean;
  setScrollToNewest: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ExampleChatQuestion {
  question: string;
  chatOption: SearchTopic;
}

const ChatPage = (props: IChatPageProps) => {
  const [selectedOption, setSelectedOption] = useState<SearchTopic>(SearchTopics.None);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [forceOpenDetailTab, setForceOpenDetailTab] = useState(0); // Open even if same row is clicked
  const location = useLocation();
  const { messages, setMessages, setChatId, setGeminiChat } = useContext(MessageContext);
  const isSmallScreen = useMediaQuery(MobileConstants.MOBILE_WIDTH_BREAKPOINT);
  const logger = useLogger('ChatPage');

  useEffect(() => {
    if (messages.length > 0) {
      if (props.scrollToNewest) {
        scroller.scrollTo('last-user-message', {
          duration: 500,
          delay: 100,
          smooth: true,
          containerId: 'chatbox',
        });
      } else {
        scroller.scrollTo('first-user-message', {
          containerId: 'chatbox',
        });
      }
    }
    const firstResponse = messages.find((m) => !m.isUser); // All bot responses have the same topic
    if (firstResponse && !firstResponse.isUser) {
      setSelectedOption(firstResponse.topic);
    }

    loadSingleResult();
  }, [messages, props.scrollToNewest]);

  /**
   * If the last bot message has 1 result, load it into the detail panel
   */
  const loadSingleResult = () => {
    logger.AI.info('Loading single result into detail panel');

    const botMessages = messages.filter((m) => !m.isUser) as IBotMessage[];
    const lastBotMessage = botMessages[botMessages.length - 1];

    if (lastBotMessage?.results?.length === 1) {
      setSelectedData(lastBotMessage.results[0]);
    } else {
      setSelectedData(null);
    }
  };

  useEffect(() => {
    newChat();
    setSelectedData(null);
  }, [location.key]);

  const newChat = (option: SearchTopic = SearchTopics.None) => {
    logger.AI.info('New chat - clearing screen');

    setMessages([]);
    setChatId(undefined);
    setSelectedOption(option);
    setGeminiChat([]);
  };

  useEffect(() => {
    if (messages.length === 0) {
      setSelectedData(null);
    }
  }, [selectedOption]);

  return (
    <Box height={'100%'} width={'100%'} overflow={'visible'}>
      <Split
        sizes={isSmallScreen ? [90, 10] : [60, 40]}
        minSize={350} // avoid img overflow in product detail panel
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        style={{ display: 'flex', height: '100%' }}
        gutterSize={10}
        gutterAlign="center"
      >
        <Chat
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          setSelectedData={setSelectedData}
          setScrollToNewest={props.setScrollToNewest}
          newChat={newChat}
          setForceOpenDetailTab={setForceOpenDetailTab}
        />
        <RightPanel selectedOption={selectedOption} data={selectedData} forceOpenDetailTab={forceOpenDetailTab} />
      </Split>
    </Box>
  );
};

export default ChatPage;
